import { memo, useRef } from "react";
import styled, { useTheme } from "styled-components";
import type { NormalizedSeries } from "./seriesData";
import {
  measureTimelineCanvas,
  prepareTimelineCanvas,
  useTimelineRowCanvas,
  type TimelineCanvasGeometry,
} from "../MediaTimelineBar/timelineRowCanvas";
import { createSeriesPathCache, paintSeriesCanvas } from "./seriesCanvasPaint";
import { resolveCanvasToken } from "../MediaTimelineBar/timelineTickCanvas";
import type {
  MediaTimelineSegment,
  MediaTimelineSeriesDomain,
} from "../MediaTimelineBar/timelineTypes";

const LineCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

interface Props {
  gutter?: number;
  series: readonly NormalizedSeries[];
  domain: MediaTimelineSeriesDomain | null;
  viewportStart: number;
  viewportDuration: number;
  /** Visible bands beneath the lines, in paint order. */
  bands?: readonly MediaTimelineSegment[];
  emphasizedSeriesId?: string | null;
  /** The emphasized line is in another lane of the plot; every line here recedes. */
  emphasisElsewhere?: boolean;
}

/** One backing bitmap per row; playback and range edits do not invalidate the lines. */
export const SeriesCanvas = memo(function SeriesCanvas({
  series,
  gutter,
  domain,
  viewportStart,
  viewportDuration,
  bands,
  emphasizedSeriesId = null,
  emphasisElsewhere = false,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();
  // Legend emphasis and theme changes repaint; they must not walk the samples again.
  const pathCacheRef = useRef(createSeriesPathCache());
  const lastPaintRef = useRef<
    | {
        geometry: TimelineCanvasGeometry;
        palette: ReturnType<typeof paintSeriesCanvas>;
      }
    | undefined
  >(undefined);
  useTimelineRowCanvas(
    canvasRef,
    (canvas, invalidated) => {
      const geometry = measureTimelineCanvas(canvas);
      if (!geometry) return false;
      const last = lastPaintRef.current;
      if (
        !invalidated &&
        last &&
        last.geometry.width === geometry.width &&
        last.geometry.height === geometry.height &&
        last.geometry.bitmapWidth === geometry.bitmapWidth &&
        last.geometry.bitmapHeight === geometry.bitmapHeight &&
        Array.from(last.palette).every(
          ([token, value]) => value === resolveCanvasToken(token, canvas),
        )
      )
        return true;
      const context = prepareTimelineCanvas(canvas, geometry);
      if (!context) return false;
      const palette = paintSeriesCanvas(context, series, {
        gutter,
        width: geometry.width,
        height: geometry.height,
        viewportStart,
        viewportDuration,
        domain,
        theme,
        bands,
        emphasizedSeriesId,
        emphasisElsewhere,
        pathCache: pathCacheRef.current,
      });
      lastPaintRef.current = { geometry, palette };
      return true;
    },
    [
      series,
      domain,
      viewportStart,
      viewportDuration,
      theme,
      bands,
      gutter,
      emphasizedSeriesId,
      emphasisElsewhere,
    ],
  );
  return <LineCanvas ref={canvasRef} aria-hidden="true" data-media-series-canvas="" />;
});
